/**
 * Alshallal Pastry Company HR System - My Profile Page
 * Allows employees to view their own profile information (read-only)
 * Phase 23: Added Documents section for employees to view their documents
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Calendar,
  CreditCard,
  Shield,
  AlertCircle,
  Loader2,
  FileText,
  MapPin,
  Users,
  RefreshCw,
  FolderOpen,
  Download,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  File
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TwoStepSignInCard } from '@/components/security/TwoStepSignInCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { isExpired } from '@/lib/expiry';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper to mask sensitive values
const maskValue = (value, showLast = 4) => {
  if (!value) return '—';
  if (value.includes('***')) return value; // Already masked
  const str = String(value);
  if (str.length <= showLast) return str;
  return '•'.repeat(str.length - showLast) + str.slice(-showLast);
};

// Format date safely
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
};

// Status badge colors
const getStatusBadge = (status) => {
  const styles = {
    active: 'bg-green-100 text-green-800',
    on_leave: 'bg-blue-100 text-blue-800',
    suspended: 'bg-orange-100 text-orange-800',
    terminated: 'bg-red-100 text-red-800',
    resigned: 'bg-gray-100 text-gray-800',
  };
  return styles[status] || 'bg-gray-100 text-gray-800';
};

// Info row component
const InfoRow = ({ label, value, icon: Icon, isRTL }) => (
  <div className="flex items-start gap-3 py-2">
    {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="font-medium break-words">{value || '—'}</p>
    </div>
  </div>
);

const MyProfilePage = () => {
  const { token, user } = useAuth();
  const { t, isRTL, language } = useLanguage();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, document: null });

  // Annual leave (2026-09-17): the employee sees when their last annual leave
  // started and when they rejoined. Day counts stay with HR.
  const [annualStatus, setAnnualStatus] = useState(null);
  
  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/employees/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      if (err.response?.status === 404) {
        setError({
          type: 'not_linked',
          message: err.response?.data?.detail?.message || 
            (isRTL ? 'لم يتم ربط حسابك بسجل موظف' : 'Your account is not linked to an employee record')
        });
      } else {
        setError({
          type: 'error',
          message: isRTL ? 'فشل في تحميل الملف الشخصي' : 'Failed to load profile'
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch documents for the employee
  const fetchDocuments = useCallback(async () => {
    try {
      setDocumentsLoading(true);
      const response = await axios.get(`${API_URL}/documents/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  }, [token]);
  
  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(
        `${API_URL}/documents/${doc.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };
  
  useEffect(() => {
    if (token) {
      fetchProfile();
      fetchDocuments();
      axios.get(`${API_URL}/leave/annual-status/my`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setAnnualStatus(res.data))
        .catch(() => setAnnualStatus(null));
    }
  }, [token, fetchDocuments]); // eslint-disable-line react-hooks/exhaustive-deps
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (error) {
    // Super Admin doesn't need an employee profile - show admin profile instead
    if (error.type === 'not_linked' && user?.role === 'super_admin') {
      return (
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6" data-testid="my-profile-page">
          {/* Super Admin Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold truncate">
                  {user.first_name} {user.last_name}
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground">
                  {isRTL ? 'المسؤول الأعلى للنظام' : 'System Super Administrator'}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <Badge className="bg-purple-100 text-purple-700">
                    {isRTL ? 'المسؤول الأعلى' : 'Super Admin'}
                  </Badge>
                  <Badge variant="outline" className="border-purple-300">
                    {isRTL ? 'صلاحيات كاملة' : 'Full Authority'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Super Admin Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-purple-600" />
                {isRTL ? 'معلومات حساب المسؤول' : 'Administrator Account'}
              </CardTitle>
              <CardDescription>
                {isRTL 
                  ? 'أنت مسؤول النظام الأعلى مع صلاحيات كاملة على جميع وظائف النظام'
                  : 'You are the System Super Administrator with full authority over all system functions'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoRow
                  icon={Mail}
                  label={isRTL ? 'البريد الإلكتروني' : 'Email'}
                  value={user.email}
                  isRTL={isRTL}
                />
                <InfoRow
                  icon={Shield}
                  label={isRTL ? 'الدور' : 'Role'}
                  value={isRTL ? 'المسؤول الأعلى' : 'Super Administrator'}
                  isRTL={isRTL}
                />
                <InfoRow
                  icon={Calendar}
                  label={isRTL ? 'تاريخ الإنشاء' : 'Account Created'}
                  value={user.created_at ? format(new Date(user.created_at), 'PP') : '—'}
                  isRTL={isRTL}
                />
                <InfoRow
                  icon={Clock}
                  label={isRTL ? 'آخر تسجيل دخول' : 'Last Login'}
                  value={user.last_login ? format(new Date(user.last_login), 'PPp') : '—'}
                  isRTL={isRTL}
                />
              </div>

              <Separator className="my-4" />

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  {isRTL ? 'صلاحيات المسؤول الأعلى' : 'Super Admin Privileges'}
                </h4>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• {isRTL ? 'الوصول الكامل لجميع سجلات الموظفين' : 'Full access to all employee records'}</li>
                  <li>• {isRTL ? 'إدارة المستخدمين والأدوار' : 'User and role management'}</li>
                  <li>• {isRTL ? 'تكوين إعدادات النظام' : 'System configuration settings'}</li>
                  <li>• {isRTL ? 'إدارة المستندات والقوالب' : 'Document and template management'}</li>
                  <li>• {isRTL ? 'سجلات التدقيق والتقارير' : 'Audit logs and reports'}</li>
                  <li>• {isRTL ? 'تجاوز جميع القيود' : 'Override all restrictions'}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <TwoStepSignInCard isRTL={isRTL} />
        </div>
      );
    }

    return (
      <div className="p-6 space-y-6">
        <Card className="max-w-lg mx-auto">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {error.type === 'not_linked' 
                  ? (isRTL ? 'لم يتم ربط الحساب' : 'Account Not Linked')
                  : (isRTL ? 'خطأ' : 'Error')
                }
              </h3>
              <p className="text-muted-foreground mb-4">{error.message}</p>
              <Button variant="outline" onClick={fetchProfile}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {isRTL ? 'إعادة المحاولة' : 'Try Again'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* An eligible admin with no employee record still has to be able to
            reach their own security settings. */}
        <div className="max-w-lg mx-auto">
          <TwoStepSignInCard isRTL={isRTL} />
        </div>
      </div>
    );
  }
  
  if (!profile) return null;
  
  const personal = profile.personal || {};
  const identity = profile.identity || {};
  const employment = profile.employment || {};
  const bank = profile.bank || {};
  const compensation = profile.compensation || {};
  
  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6" data-testid="my-profile-page">
      {/* Header - responsive */}
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <User className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold truncate">
              {isRTL 
                ? `${personal.first_name_ar || personal.first_name} ${personal.last_name_ar || personal.last_name}`
                : `${personal.first_name} ${personal.last_name}`
              }
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground truncate">{employment.job_title || '—'}</p>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge className={getStatusBadge(profile.status)}>
                {profile.status?.replace('_', ' ') || 'Unknown'}
              </Badge>
              {profile.employee_number && (
                <Badge variant="outline">#{profile.employee_number}</Badge>
              )}
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={fetchProfile} disabled={loading} className="w-full sm:w-auto">
          <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5" />
              {isRTL ? 'المعلومات الأساسية' : 'Basic Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoRow 
              label={isRTL ? 'الاسم الكامل (إنجليزي)' : 'Full Name (English)'} 
              value={`${personal.first_name || ''} ${personal.middle_name || ''} ${personal.last_name || ''}`.trim()} 
            />
            {(personal.first_name_ar || personal.last_name_ar) && (
              <InfoRow 
                label={isRTL ? 'الاسم الكامل (عربي)' : 'Full Name (Arabic)'} 
                value={`${personal.first_name_ar || ''} ${personal.middle_name_ar || ''} ${personal.last_name_ar || ''}`.trim()} 
              />
            )}
            <InfoRow 
              label={isRTL ? 'الجنسية' : 'Nationality'} 
              value={personal.nationality === 'saudi' ? (isRTL ? 'سعودي' : 'Saudi') : (isRTL ? 'غير سعودي' : 'Non-Saudi')} 
            />
            <InfoRow 
              label={isRTL ? 'تاريخ الميلاد' : 'Date of Birth'} 
              value={formatDate(personal.date_of_birth)} 
              icon={Calendar}
            />
            <InfoRow 
              label={isRTL ? 'الجنس' : 'Gender'} 
              value={personal.gender ? (personal.gender === 'male' ? (isRTL ? 'ذكر' : 'Male') : (isRTL ? 'أنثى' : 'Female')) : '—'} 
            />
            <InfoRow 
              label={isRTL ? 'الحالة الاجتماعية' : 'Marital Status'} 
              value={personal.marital_status || '—'} 
            />
          </CardContent>
        </Card>
        
        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="h-5 w-5" />
              {isRTL ? 'معلومات الاتصال' : 'Contact Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoRow 
              label={isRTL ? 'البريد الإلكتروني' : 'Email'} 
              value={profile.email} 
              icon={Mail}
            />
            <InfoRow 
              label={isRTL ? 'رقم الهاتف' : 'Phone Number'} 
              value={personal.phone} 
              icon={Phone}
            />
            <InfoRow 
              label={isRTL ? 'البريد الشخصي' : 'Personal Email'} 
              value={personal.personal_email} 
              icon={Mail}
            />
            <InfoRow 
              label={isRTL ? 'العنوان' : 'Address'} 
              value={[personal.address, personal.city, personal.country].filter(Boolean).join(', ')} 
              icon={MapPin}
            />
          </CardContent>
        </Card>
        
        {/* Employment Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Briefcase className="h-5 w-5" />
              {isRTL ? 'معلومات التوظيف' : 'Employment Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoRow 
              label={isRTL ? 'المسمى الوظيفي' : 'Job Title'} 
              value={employment.job_title} 
              icon={Briefcase}
            />
            <InfoRow 
              label={isRTL ? 'القسم' : 'Department'} 
              value={employment.department} 
              icon={Building2}
            />
            <InfoRow 
              label={isRTL ? 'نوع العقد' : 'Contract Type'} 
              value={employment.contract_type?.replace('_', ' ')} 
            />
            <InfoRow 
              label={isRTL ? 'تاريخ التعيين' : 'Hire Date'} 
              value={formatDate(employment.hire_date)} 
              icon={Calendar}
            />
            <InfoRow 
              label={isRTL ? 'بداية العقد' : 'Contract Start'} 
              value={formatDate(employment.contract_start_date)} 
            />
            <InfoRow 
              label={isRTL ? 'نهاية العقد' : 'Contract End'} 
              value={formatDate(employment.contract_end_date)} 
            />
            <InfoRow 
              label={isRTL ? 'موقع العمل' : 'Work Location'} 
              value={employment.work_location} 
              icon={MapPin}
            />
            <InfoRow 
              label={isRTL ? 'مركز التكلفة' : 'Cost Center'} 
              value={employment.cost_center} 
            />
            <InfoRow 
              label={isRTL ? 'ساعات العمل اليومية' : 'Daily Working Hours'} 
              value={employment.daily_working_hours ? `${employment.daily_working_hours} ${isRTL ? 'ساعات/يوم' : 'hours/day'}` : '8 hours/day'} 
            />
            {annualStatus && (
              <div data-testid="my-annual-leave-dates">
                <Separator className="my-2" />
                <InfoRow
                  label={isRTL ? 'بداية آخر إجازة سنوية' : 'Last Annual Leave Start'}
                  value={annualStatus.has_ever_taken_annual_leave
                    ? formatDate(annualStatus.last_leave_start)
                    : (isRTL ? 'لا توجد إجازة سنوية مسجلة' : 'No annual leave recorded yet')}
                  icon={Calendar}
                />
                <InfoRow
                  label={isRTL ? 'تاريخ المباشرة بعد الإجازة' : 'Rejoin Date'}
                  value={annualStatus.has_ever_taken_annual_leave
                    ? formatDate(annualStatus.last_leave_rejoin)
                    : '—'}
                  icon={Calendar}
                />
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Saudi Compliance / Identity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="h-5 w-5" />
              {isRTL ? 'المستندات الرسمية' : 'Official Documents'}
            </CardTitle>
            <CardDescription>
              {isRTL ? 'المعلومات محمية ومخفية جزئياً' : 'Information is protected and partially masked'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            {personal.nationality === 'saudi' ? (
              <>
                <InfoRow 
                  label={isRTL ? 'رقم الهوية الوطنية' : 'National ID'} 
                  value={identity.national_id || '—'} 
                  icon={CreditCard}
                />
                <InfoRow 
                  label={isRTL ? 'تاريخ انتهاء الهوية' : 'National ID Expiry'} 
                  value={formatDate(identity.national_id_expiry)} 
                />
              </>
            ) : (
              <>
                <InfoRow 
                  label={isRTL ? 'رقم الإقامة' : 'Iqama Number'} 
                  value={identity.iqama || '—'} 
                  icon={CreditCard}
                />
                <InfoRow 
                  label={isRTL ? 'تاريخ انتهاء الإقامة' : 'Iqama Expiry'} 
                  value={formatDate(identity.iqama_expiry)} 
                />
              </>
            )}
            <Separator className="my-2" />
            <InfoRow 
              label={isRTL ? 'رقم جواز السفر' : 'Passport Number'} 
              value={identity.passport_number || '—'} 
              icon={FileText}
            />
            <InfoRow 
              label={isRTL ? 'تاريخ انتهاء الجواز' : 'Passport Expiry'} 
              value={formatDate(identity.passport_expiry)} 
            />
          </CardContent>
        </Card>
        
        {/* Bank Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CreditCard className="h-5 w-5" />
              {isRTL ? 'المعلومات البنكية' : 'Bank Information'}
            </CardTitle>
            <CardDescription>
              {isRTL ? 'معلومات الحساب البنكي للرواتب' : 'Bank account for salary payments'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoRow 
              label={isRTL ? 'اسم البنك' : 'Bank Name'} 
              value={bank.bank_name} 
            />
            <InfoRow 
              label={isRTL ? 'رقم الآيبان' : 'IBAN'} 
              value={bank.iban || '—'} 
              icon={CreditCard}
            />
          </CardContent>
        </Card>
        
        {/* Compensation Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CreditCard className="h-5 w-5" />
              {isRTL ? 'التعويضات' : 'Compensation'}
            </CardTitle>
            <CardDescription>
              {isRTL ? 'تفاصيل الراتب والبدلات' : 'Salary and allowance details'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoRow 
              label={isRTL ? 'الراتب الأساسي' : 'Basic Salary'} 
              value={compensation.basic_salary ? `${compensation.basic_salary.toLocaleString()} ${compensation.currency || 'SAR'}` : '—'} 
            />
            <InfoRow 
              label={isRTL ? 'بدل السكن' : 'Housing Allowance'} 
              value={compensation.housing_allowance ? `${compensation.housing_allowance.toLocaleString()} ${compensation.currency || 'SAR'}` : '—'} 
            />
            <InfoRow 
              label={isRTL ? 'بدل المواصلات' : 'Transportation Allowance'} 
              value={compensation.transportation_allowance ? `${compensation.transportation_allowance.toLocaleString()} ${compensation.currency || 'SAR'}` : '—'} 
            />
            <InfoRow 
              label={isRTL ? 'بدلات أخرى' : 'Other Allowances'} 
              value={compensation.other_allowances ? `${compensation.other_allowances.toLocaleString()} ${compensation.currency || 'SAR'}` : '—'} 
            />
            <Separator className="my-2" />
            <InfoRow 
              label={isRTL ? 'إجمالي الحزمة' : 'Total Package'} 
              value={(() => {
                const total = (compensation.basic_salary || 0) + 
                              (compensation.housing_allowance || 0) + 
                              (compensation.transportation_allowance || 0) + 
                              (compensation.other_allowances || 0);
                return total > 0 ? `${total.toLocaleString()} ${compensation.currency || 'SAR'}` : '—';
              })()} 
            />
            <Separator className="my-2" />
            <InfoRow 
              label={isRTL ? 'ساعات العمل الأسبوعية' : 'Weekly Working Hours'} 
              value={compensation.working_hours_per_week ? `${compensation.working_hours_per_week} ${isRTL ? 'ساعة' : 'hours'}` : '—'} 
            />
            <InfoRow 
              label={isRTL ? 'جدول العمل' : 'Work Schedule'} 
              value={compensation.work_schedule || '—'} 
            />
          </CardContent>
        </Card>
        
        {/* Emergency Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" />
              {isRTL ? 'جهة الاتصال في حالات الطوارئ' : 'Emergency Contact'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoRow 
              label={isRTL ? 'الاسم' : 'Name'} 
              value={personal.emergency_contact_name} 
              icon={User}
            />
            <InfoRow 
              label={isRTL ? 'رقم الهاتف' : 'Phone'} 
              value={personal.emergency_contact_phone} 
              icon={Phone}
            />
            <InfoRow 
              label={isRTL ? 'صلة القرابة' : 'Relationship'} 
              value={personal.emergency_contact_relation} 
            />
          </CardContent>
        </Card>
      </div>
      
      {/* Custom Fields */}
      {profile.custom_fields && profile.custom_fields.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5" />
              {isRTL ? 'معلومات إضافية' : 'Additional Information'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profile.custom_fields.map((field) => (
                <div key={field.field_id} className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    {isRTL && field.field_name_ar ? field.field_name_ar : field.field_name}
                  </p>
                  <p className="font-medium">
                    {field.field_type === 'date' 
                      ? formatDate(field.value)
                      : field.field_type === 'boolean'
                        ? (field.value ? (isRTL ? 'نعم' : 'Yes') : (isRTL ? 'لا' : 'No'))
                        : field.value || '—'
                    }
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* My Documents Section - Read Only */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FolderOpen className="h-5 w-5" />
              {isRTL ? 'مستنداتي' : 'My Documents'}
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchDocuments} 
              disabled={documentsLoading}
            >
              <RefreshCw className={cn("h-4 w-4", documentsLoading && "animate-spin")} />
            </Button>
          </div>
          <CardDescription>
            {isRTL 
              ? 'المستندات المرفوعة من قسم الموارد البشرية'
              : 'Documents uploaded by HR department'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {documentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8">
              <File className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">
                {isRTL ? 'لا توجد مستندات بعد' : 'No documents yet'}
              </p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                {isRTL 
                  ? 'سيتم عرض المستندات هنا عندما يقوم قسم الموارد البشرية برفعها'
                  : 'Documents will appear here once HR uploads them'
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {documents.map((doc) => {
                const STATUS_STYLES = {
                  pending: { bg: 'bg-amber-100 text-amber-800', icon: Clock },
                  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle },
                  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle },
                };
                const StatusIcon = STATUS_STYLES[doc.status]?.icon || Clock;
                const expired = isExpired(doc);
                
                return (
                  <div 
                    key={doc.id} 
                    className="p-3 border rounded-lg hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-slate-100 rounded">
                        <File className="h-4 w-4 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {doc.document_type_name || 'Document'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {doc.file_name}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          {isRTL ? 'الحالة' : 'Status'}
                        </span>
                        <Badge className={cn("text-xs", STATUS_STYLES[doc.status]?.bg)}>
                          <StatusIcon className="h-3 w-3 me-1" />
                          {doc.status === 'pending' 
                            ? (isRTL ? 'قيد المراجعة' : 'Pending')
                            : doc.status === 'approved'
                              ? (isRTL ? 'موافق' : 'Approved')
                              : (isRTL ? 'مرفوض' : 'Rejected')
                          }
                        </Badge>
                      </div>
                      
                      {doc.expiry_date && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">
                            {isRTL ? 'تاريخ الانتهاء' : 'Expires'}
                          </span>
                          <span className={cn(
                            expired && "text-red-600 font-medium"
                          )}>
                            {formatDate(doc.expiry_date)}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-8 text-xs"
                        onClick={() => setViewDialog({ open: true, document: doc })}
                      >
                        <Eye className="h-3 w-3 me-1" />
                        {isRTL ? 'عرض' : 'View'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => handleDownload(doc)}
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Document View Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => !open && setViewDialog({ open: false, document: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تفاصيل المستند' : 'Document Details'}</DialogTitle>
          </DialogHeader>
          
          {viewDialog.document && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">{isRTL ? 'نوع المستند' : 'Document Type'}</p>
                  <p className="font-medium">{viewDialog.document.document_type_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{isRTL ? 'اسم الملف' : 'File Name'}</p>
                  <p className="font-medium truncate">{viewDialog.document.file_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{isRTL ? 'تاريخ الرفع' : 'Upload Date'}</p>
                  <p className="font-medium">{formatDate(viewDialog.document.uploaded_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{isRTL ? 'الحالة' : 'Status'}</p>
                  <Badge className={cn(
                    viewDialog.document.status === 'approved' && 'bg-green-100 text-green-800',
                    viewDialog.document.status === 'pending' && 'bg-amber-100 text-amber-800',
                    viewDialog.document.status === 'rejected' && 'bg-red-100 text-red-800'
                  )}>
                    {viewDialog.document.status === 'pending' 
                      ? (isRTL ? 'قيد المراجعة' : 'Pending')
                      : viewDialog.document.status === 'approved'
                        ? (isRTL ? 'موافق' : 'Approved')
                        : (isRTL ? 'مرفوض' : 'Rejected')
                    }
                  </Badge>
                </div>
                {viewDialog.document.expiry_date && (
                  <div>
                    <p className="text-muted-foreground">{isRTL ? 'تاريخ الانتهاء' : 'Expiry Date'}</p>
                    <p className={cn(
                      "font-medium",
                      isExpired(viewDialog.document) && "text-red-600"
                    )}>
                      {formatDate(viewDialog.document.expiry_date)}
                    </p>
                  </div>
                )}
              </div>

              {viewDialog.document.notes && (
                <div>
                  <p className="text-muted-foreground text-sm mb-1">{isRTL ? 'ملاحظات' : 'Notes'}</p>
                  <p className="text-sm bg-muted p-2 rounded">{viewDialog.document.notes}</p>
                </div>
              )}

              {viewDialog.document.review_reason && (
                <div>
                  <p className="text-muted-foreground text-sm mb-1">{isRTL ? 'سبب المراجعة' : 'Review Reason'}</p>
                  <p className={cn(
                    "text-sm p-2 rounded",
                    viewDialog.document.status === 'approved' ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                  )}>
                    {viewDialog.document.review_reason}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog({ open: false, document: null })}>
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
            {viewDialog.document && (
              <Button onClick={() => handleDownload(viewDialog.document)}>
                <Download className="h-4 w-4 me-2" />
                {isRTL ? 'تحميل' : 'Download'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TwoStepSignInCard isRTL={isRTL} />
    </div>
  );
};

export default MyProfilePage;
